import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { AccessUser, BackofficeUserLoginLog } from '../../../../../core/models/system-settings/access-users.model';
import { ApiSystemSettingsService } from '../../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperAccessUsersService } from '../../../../../core/mappers/system-settings/mapper-access-users.service';

@Component({
  selector: 'app-modal-user-login-logs',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal-user-login-logs.component.html',
  styleUrl: './modal-user-login-logs.component.css',
})
export class ModalUserLoginLogsComponent implements OnInit {
  @Input({ required: true }) user!: AccessUser;
  @Output() close = new EventEmitter<void>();

  logs = signal<BackofficeUserLoginLog[]>([]);
  isLoading = signal(false);
  currentPage = signal(1);
  pageSize = 20;
  totalItems = signal(0);
  totalPages = signal(0);
  eventType = '';
  searchEventText = '';
  openEventDropdown = false;

  eventTypeOptions = [
    { value: '', label: 'ทุกประเภท' },
    { value: 'login_success', label: 'เข้าสู่ระบบ' },
    { value: 'login_failed', label: 'เข้าไม่สำเร็จ' },
    { value: 'logout', label: 'ออกจากระบบ' },
    { value: 'session_replaced', label: 'ถูกเตะเครื่อง' },
    { value: 'password_changed', label: 'เปลี่ยนรหัส' },
    { value: 'force_repassword', label: 'บังคับเปลี่ยนรหัส' },
  ];

  private apiService = inject(ApiSystemSettingsService);
  private mapper = inject(MapperAccessUsersService);
  private toast = inject(ToastService);

  ngOnInit(): void {
    this.searchEventText = this.eventLabel(this.eventType);
    this.loadLogs();
  }

  eventLabel(type: string): string {
    return this.eventTypeOptions.find((o) => o.value === type)?.label || type || 'ทุกประเภท';
  }

  get filteredEventOptions(): { value: string; label: string }[] {
    const query = (this.searchEventText || '').toLowerCase().trim();
    if (!query) return this.eventTypeOptions;
    return this.eventTypeOptions.filter((o) => o.label.toLowerCase().includes(query));
  }

  eventBadgeClass(type: string): string {
    switch (type) {
      case 'login_success':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'login_failed':
        return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
      case 'password_changed':
      case 'force_repassword':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      case 'session_replaced':
        return 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300';
      default:
        return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
    }
  }

  onEventInputFocus(): void {
    this.openEventDropdown = true;
  }

  closeEventDropdown(): void {
    const match = this.eventTypeOptions.find(
      (o) => o.label.toLowerCase() === this.searchEventText.trim().toLowerCase(),
    );
    if (match) {
      this.eventType = match.value;
      this.searchEventText = match.label;
    } else {
      this.searchEventText = this.eventLabel(this.eventType);
    }
    this.openEventDropdown = false;
  }

  selectEventType(value: string): void {
    this.eventType = value;
    this.searchEventText = this.eventLabel(value);
    this.openEventDropdown = false;
    this.onFilter();
  }

  onFilter(): void {
    this.currentPage.set(1);
    this.loadLogs();
  }

  setPage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.currentPage.set(page);
    this.loadLogs();
  }

  onClose(): void {
    this.close.emit();
  }

  private loadLogs(): void {
    this.isLoading.set(true);
    this.apiService
      .getBackofficeUserLoginLogsForTable(
        this.user.id,
        this.currentPage(),
        this.pageSize,
        this.eventType || undefined,
      )
      .subscribe({
        next: (res) => {
          this.logs.set((res.data || []).map((d) => this.mapper.mapLoginLogFromDto(d)));
          this.totalItems.set(res.total ?? 0);
          this.totalPages.set(res.totalPages ?? 0);
          this.isLoading.set(false);
        },
        error: () => {
          this.toast.error('ไม่สามารถโหลด log ได้');
          this.isLoading.set(false);
        },
      });
  }
}
